import * as THREE from 'three';
import type { QualityLevel } from '../app/urlParams';
import { blendRig } from './cameraRig';
import { safeSize } from './frame';
import { PostChain } from './post/PostChain';

const PIXEL_RATIO_CAP: Record<QualityLevel, number> = { low: 1, med: 1.5, high: 2 };

/** Owns the WebGL renderer, camera, post chain, quality level and resize handling. */
export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly camera = new THREE.PerspectiveCamera(45, 1, 0.1, 50000);
  private quality: QualityLevel;
  private post: PostChain | undefined;

  constructor(container: HTMLElement, quality: QualityLevel = 'med') {
    this.quality = quality;
    // MSAA happens in the composer; the default framebuffer only receives the final image.
    this.renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
    this.renderer.toneMapping = THREE.NoToneMapping; // AgX runs in the post chain
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.type = THREE.PCFShadowMap; // r186: PCFSoft was removed
    container.appendChild(this.renderer.domElement);
    this.applyQuality();
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  /** Builds the post chain for the scene (call once the scene exists). */
  attach(scene: THREE.Scene): void {
    this.post = new PostChain(this.renderer, scene, this.camera, this.quality);
    this.resize();
  }

  get level(): QualityLevel {
    return this.quality;
  }

  setQuality(q: QualityLevel): void {
    this.quality = q;
    this.applyQuality();
    this.post?.setQuality(q);
    this.resize();
  }

  /** Shadows: off on low. */
  private applyQuality(): void {
    this.renderer.shadowMap.enabled = this.quality !== 'low';
  }

  get shadowMapSize(): number {
    return this.quality === 'high' ? 2048 : 1024;
  }

  resize(): void {
    const { width, height } = safeSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, PIXEL_RATIO_CAP[this.quality]));
    this.renderer.setSize(width, height, false);
    this.post?.setSize(width, height);
    this.camera.aspect = width / height;
    const rig = blendRig(this.camera.aspect);
    this.camera.fov = rig.fovDeg;
    this.camera.position.set(...rig.position);
    this.camera.lookAt(...rig.target);
    this.camera.updateProjectionMatrix();
  }

  /** Eye adaptation + grade LUT for the current sun altitude. */
  updatePost(exposure: number, sunAltDeg: number, forceLut = false): void {
    this.post?.update(exposure, sunAltDeg, forceLut);
  }

  render(scene: THREE.Scene, dtSec = 1 / 60): void {
    if (this.post) this.post.render(dtSec);
    else this.renderer.render(scene, this.camera);
  }
}
